// src/components/network/interactions/ReactionList.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Building, User, ThumbsUp } from 'lucide-react';
import { db } from '../../../firebase/config';
import { 
  collection, query, where, getDocs, orderBy, limit, 
  doc, getDoc 
} from 'firebase/firestore';

const ReactionList = ({ postId, onClose }) => {
  const [reactions, setReactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchReactions = async () => {
      try {
        setLoading(true);
        
        // Get likes from postLikes collection
        const likesQuery = query(
          collection(db, 'postLikes'),
          where('postId', '==', postId),
          orderBy('createdAt', 'desc')
        );
        
        const likesSnapshot = await getDocs(likesQuery);
        
        // Get user/business info for each like
        const reactionsPromises = likesSnapshot.docs.map(async (likeDoc) => {
          const likeData = likeDoc.data();
          
          // Get user/business data
          let userData = { id: likeData.userId, name: 'Unknown User' };
          
          try {
            // First check if it's a business
            const businessRef = doc(db, 'businesses', likeData.userId);
            const businessSnap = await getDoc(businessRef);
            
            if (businessSnap.exists()) {
              userData = {
                id: businessSnap.id,
                ...businessSnap.data(),
                type: 'business'
              };
            } else {
              // If not a business, check users collection
              const userRef = doc(db, 'users', likeData.userId);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const user = userSnap.data();
                userData = {
                  id: userSnap.id,
                  name: user.displayName || user.email,
                  email: user.email,
                  type: 'user'
                };
              }
            }
          } catch (err) {
            console.error('Error fetching reaction user:', err);
          }
          
          return {
            id: likeDoc.id,
            ...likeData,
            createdAt: likeData.createdAt?.toDate() || new Date(),
            user: userData
          };
        });
        
        const processedReactions = await Promise.all(reactionsPromises);
        setReactions(processedReactions);
        
      } catch (error) {
        console.error('Error fetching reactions:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReactions();
  }, [postId]);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <ThumbsUp size={18} className="mr-2 text-[#190192]" />
            Reactions
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          {loading ? (
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center">
                  <div className="h-10 w-10 bg-gray-200 rounded-full mr-3"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : reactions.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              No reactions yet
            </p>
          ) : (
            <ul className="space-y-4">
              {reactions.map(reaction => (
                <li key={reaction.id} className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                    {reaction.user.type === 'business' && reaction.user.businessLogoUrl ? (
                      <img 
                        src={reaction.user.businessLogoUrl} 
                        alt={reaction.user.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : reaction.user.type === 'business' ? (
                      <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                        <Building size={20} />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600">
                        <User size={20} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {reaction.user.type === 'business' ? (
                      <Link
                        to={`/network/profile/${reaction.user.id}`}
                        className="font-medium text-gray-900 hover:text-[#190192] block truncate"
                      >
                        {reaction.user.name}
                      </Link>
                    ) : (
                      <span className="font-medium text-gray-900 block truncate">
                        {reaction.user.name}
                      </span>
                    )}
                    {reaction.user.type === 'business' && reaction.user.industry && (
                      <p className="text-gray-500 text-sm truncate">
                        {reaction.user.industry}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReactionList;